import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { api, saveAuth } from "@/lib/api";
import AuthSplitScreen from "@/components/AuthSplitScreen";
import ModernInput from "@/components/ModernInput";
import MagneticButton from "@/components/MagneticButton";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

// ==== Validação CPF (dígito verificador) ====
function validateCPF(cpf) {
  const d = (cpf || "").replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const size of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < size; i++) sum += parseInt(d[i], 10) * (size + 1 - i);
    let rem = (sum * 10) % 11;
    if (rem === 10) rem = 0;
    if (rem !== parseInt(d[size], 10)) return false;
  }
  return true;
}

// ==== Validação CNPJ (dígito verificador) ====
function validateCNPJ(cnpj) {
  const d = (cnpj || "").replace(/\D/g, "");
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, ...w1];
  for (const w of [w1, w2]) {
    let sum = 0;
    for (let i = 0; i < w.length; i++) sum += parseInt(d[i], 10) * w[i];
    const rem = sum % 11;
    const digit = rem < 2 ? 0 : 11 - rem;
    if (digit !== parseInt(d[w.length], 10)) return false;
  }
  return true;
}

const formatPhone = (v) => {
  let d = v.replace(/\D/g, "");
  if (d.startsWith("55")) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `+55 ${d}`;
  if (d.length <= 7) return `+55 ${d.slice(0, 2)} ${d.slice(2)}`;
  return `+55 ${d.slice(0, 2)} ${d.slice(2, 7)}-${d.slice(7)}`;
};

const formatDoc = (v) => {
  const d = v.replace(/\D/g, "");
  if (d.length <= 11) {
    return d.slice(0, 11)
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
  }
  const t = d.slice(0, 14);
  if (t.length <= 5) return `${t.slice(0, 2)}.${t.slice(2)}`;
  if (t.length <= 8) return `${t.slice(0, 2)}.${t.slice(2, 5)}.${t.slice(5)}`;
  if (t.length <= 12) return `${t.slice(0, 2)}.${t.slice(2, 5)}.${t.slice(5, 8)}/${t.slice(8)}`;
  return `${t.slice(0, 2)}.${t.slice(2, 5)}.${t.slice(5, 8)}/${t.slice(8, 12)}-${t.slice(12)}`;
};

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cpf_cnpj: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [looking, setLooking] = useState(false);
  const [docTouched, setDocTouched] = useState(false);
  const nav = useNavigate();

  const docDigits = form.cpf_cnpj.replace(/\D/g, "");
  const docKind = docDigits.length === 14 ? "cnpj" : docDigits.length === 11 ? "cpf" : null;
  const docValid = useMemo(() => {
    if (docKind === "cpf") return validateCPF(docDigits);
    if (docKind === "cnpj") return validateCNPJ(docDigits);
    return false;
  }, [docDigits, docKind]);
  const docHasError = docTouched && !docValid;

  const lookupDoc = async () => {
    setDocTouched(true);
    if (docDigits.length !== 11 && docDigits.length !== 14) return;
    if (!docValid) return;
    setLooking(true);
    try {
      if (docKind === "cnpj") {
        const { data } = await api.get(`/lookup/cnpj/${docDigits}`);
        setForm((f) => ({ ...f, name: data.razao_social || data.nome_fantasia || f.name }));
        toast.success(`CNPJ validado: ${data.razao_social}`);
      } else {
        toast.success("CPF válido");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Documento inválido");
    } finally {
      setLooking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setDocTouched(true);
    if (!docValid) {
      toast.error(
        docKind === "cpf"
          ? "CPF inválido. Corrija antes de continuar."
          : docKind === "cnpj"
          ? "CNPJ inválido. Corrija antes de continuar."
          : "Informe um CPF ou CNPJ válido"
      );
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      saveAuth(data.token, data.user);
      toast.success("Conta criada! Bem-vindo ao MercadoPagu.");
      nav("/app");
    } catch (err) {
      const d = err.response?.data?.detail;
      let msg = "Erro ao criar conta";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((e) => e.msg || JSON.stringify(e)).join(" · ");
      else if (d) msg = JSON.stringify(d);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const docError = docHasError
    ? docKind === "cnpj" || docDigits.length > 11
      ? "CNPJ inválido. Verifique os dígitos."
      : docDigits.length < 11
      ? "CPF incompleto. Digite os 11 números."
      : "CPF inválido. Verifique os dígitos."
    : null;

  const canSubmit = docValid && form.name && form.email && form.password.length >= 6 && !loading;

  return (
    <AuthSplitScreen side="right">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="label-caps mb-3 inline-flex items-center gap-2">
          <Sparkle size={12} weight="fill" className="text-[var(--accent)]" />
          Nova conta grátis
        </div>
        <h1 className="text-fluid-h2 font-display font-black text-white mb-3 wrap-safe">
          Monte o seu <span className="text-[var(--accent)]">banco</span> em 60s.
        </h1>
        <p className="text-white/60 mb-8 text-fluid-lead wrap-safe">
          Pix, transferências, funis, afiliados e área de membros — tudo debaixo da sua marca. Sem taxa de setup.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form" noValidate>
          <ModernInput
            label="Nome completo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoComplete="name"
            testid="register-name"
          />
          <ModernInput
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            autoComplete="email"
            inputMode="email"
            testid="register-email"
          />
          <ModernInput
            label="Telefone (opcional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
            autoComplete="tel"
            inputMode="tel"
            maxLength={17}
            testid="register-phone"
          />
          <ModernInput
            label="CPF ou CNPJ"
            value={form.cpf_cnpj}
            onChange={(e) => {
              setForm({ ...form, cpf_cnpj: formatDoc(e.target.value) });
              if (docTouched) setDocTouched(false);
            }}
            onBlur={lookupDoc}
            required
            inputMode="numeric"
            success={docValid && docTouched}
            error={docError}
            hint={
              looking
                ? "Consultando Receita..."
                : !docKind
                ? "Vamos validar os dígitos automaticamente"
                : null
            }
            testid="register-cpfcnpj"
          />
          <div>
            <ModernInput
              label="Senha (mínimo 6)"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              autoComplete="new-password"
              testid="register-password"
            />
            <PasswordStrengthMeter password={form.password} />
          </div>

          <MagneticButton
            as="button"
            type="submit"
            disabled={!canSubmit}
            className="btn-magnetic w-full mt-2"
            testid="register-submit"
            style={{
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Criando sua conta...
              </>
            ) : (
              <>
                Criar minha conta
                <ArrowRight size={16} weight="bold" />
              </>
            )}
          </MagneticButton>

          <p className="text-[11px] text-white/40 leading-relaxed text-center mt-3 wrap-safe">
            Ao criar, você concorda em receber comunicações da MercadoPagu.
            Sem cartão. Sem compromisso. Você fecha quando quiser.
          </p>
        </form>

        <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between gap-4 text-sm flex-wrap">
          <span className="text-white/50">Já tem conta?</span>
          <Link
            to="/login"
            className="text-[var(--accent)] font-bold hover:underline flex items-center gap-1"
            data-testid="register-to-login"
          >
            Fazer login <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </motion.div>
    </AuthSplitScreen>
  );
}
